import type { SiteLocale } from "@graphql/types/graphql";

/**
 * Office pages form a tree (root → child → grandchild). URLs must include every
 * ancestor slug (e.g. /offices/fidiaz/digital-transformation/leaf), not only the
 * immediate parent.
 *
 * Prefer the localized `slug` from the API, then match `_allSlugLocales` by locale.
 * Do not use `slugs[0]` — locale order in CMS responses is not guaranteed (often EN first).
 */
export const getOfficeSlugSegment = (
  office: { slug?: string | null; slugs?: { locale: SiteLocale; value: string }[] } | null,
  siteLocale?: SiteLocale,
): string => {
  if (!office) return "";
  if (office.slug) return office.slug;
  if (office.slugs?.length) {
    if (siteLocale) {
      const match = office.slugs.find((s) => s.locale === siteLocale);
      if (match) return match.value;
    }
    return office.slugs[0].value;
  }
  return "";
};

/** All ancestors of `office` from root to `office`, inclusive. */
export const getOfficeAncestorsOldestFirst = (office: any): any[] => {
  if (!office) return [];
  if (!office.parent) return [office];
  return [...getOfficeAncestorsOldestFirst(office.parent), office];
};

/** Root (brand) office slug for contact form prefill — not the leaf slug on subpages. */
export const getContactPrefillOfficeSlug = (
  office: { slug?: string | null; parent?: any; slugs?: { locale: SiteLocale; value: string }[] } | null,
  siteLocale?: SiteLocale,
): string => {
  if (!office) return "";
  const [root] = getOfficeAncestorsOldestFirst(office);
  return getOfficeSlugSegment(root, siteLocale) || getOfficeSlugSegment(office, siteLocale);
};

/**
 * Slug path for all ancestors of `item` (excluding `item`), oldest first:
 * "/root/child" when `item` is a grandchild.
 */
export const getOfficeParentPathPrefix = (
  item: { parent?: any } | null,
  siteLocale?: SiteLocale,
): string => {
  if (!item?.parent) return "";
  const ancestors = getOfficeAncestorsOldestFirst(item.parent);
  const segments = ancestors.map((a) => getOfficeSlugSegment(a, siteLocale)).filter(Boolean);
  return segments.length ? `/${segments.join("/")}` : "";
};
